import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import PageLayout from "@/components/PageLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ROUTES } from "@/lib/routes";
import { FAQS_FULL } from "@/lib/faqs";
import { buildWhatsAppLink, SITE } from "@/lib/site";

const fade = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export const FaqPage = () => {
  return (
    <PageLayout
      title={ROUTES.faq.title}
      description={ROUTES.faq.description}
      testId="faq-page"
    >
      {/* Hero */}
      <section className="pb-10 md:pb-16">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div {...fade}>
            <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
              Questions mothers ask us
            </div>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal leading-[1.08] tracking-tight">
              Prasava Raksha:{" "}
              <span className="italic text-sage-700">Questions</span> Mothers
              Ask Us.
            </h1>
            <p className="mt-5 text-base md:text-lg text-charcoal/70 leading-relaxed max-w-2xl">
              Everything families usually want to know before booking a stay at
              Kakkanat. If your question is not here, message us on WhatsApp,
              we usually reply within the hour.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Accordion */}
      <section className="py-10 md:py-16 bg-ivory">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div {...fade}>
            <Accordion
              type="multiple"
              defaultValue={FAQS_FULL.map((f) => f.id)}
              className="w-full"
            >
              {FAQS_FULL.map((f) => (
                <AccordionItem
                  key={f.id}
                  value={f.id}
                  className="border-b border-sage/20"
                  data-testid={`faq-page-item-${f.id}`}
                >
                  <AccordionTrigger className="py-6 text-left font-serif text-lg md:text-xl text-charcoal hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-base text-charcoal/70 leading-relaxed">
                    {f.id === "cost" ? (
                      <>
                        {f.a}{" "}
                        <a
                          href="/#pricing"
                          className="text-sage hover:text-sage-700 underline underline-offset-2"
                        >
                          See all packages
                        </a>
                        .
                      </>
                    ) : (
                      f.a
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          <motion.div {...fade} className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 md:p-8 rounded-2xl bg-sand/60 border border-sage/15">
            <div>
              <div className="font-serif text-xl text-charcoal">Still wondering?</div>
              <p className="text-sm text-charcoal/65 mt-1">
                Speak with us directly on WhatsApp, 9am to 8pm.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href="/#pricing"
                className="inline-flex items-center justify-center gap-2 border border-sage/40 text-charcoal px-5 py-3 rounded-full text-sm font-medium hover:bg-sage hover:text-ivory hover:border-sage transition-colors"
                data-testid="faq-page-packages"
              >
                See packages
                <ArrowRight size={15} />
              </a>
              <a
                href={buildWhatsAppLink("default")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-sage text-ivory px-5 py-3 rounded-full text-sm font-medium hover:bg-sage-700 transition-colors"
                data-testid="faq-page-whatsapp"
              >
                WhatsApp {SITE.whatsappDisplay}
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
};

export default FaqPage;
